/**
 * 短剧管理 → AI 功能配置
 *
 * 子 Tab：
 * - 拆分镜
 * - 多帧助手
 * - 三合一融合
 *
 * 这只是一个容器组件，不引入新业务逻辑；子组件维持原有职责与状态。
 */

import { useState } from 'react';
import { Wand2, Sparkles, ToggleLeft } from 'lucide-react';

import DirectorShotSplitConfigTab from './DirectorShotSplitConfigTab';
import DirectorFrameStoryboardConfigTab from './DirectorFrameStoryboardConfigTab';
import DirectorTrinityCardsConfigTab from './DirectorTrinityCardsConfigTab';
import DirectorModeSwitchConfigTab from './DirectorModeSwitchConfigTab';

type SubTab = 'shotSplit' | 'frameStoryboard' | 'trinityCards' | 'modeSwitch';

const SUB_TABS: Array<{ key: SubTab; label: string; icon: typeof Wand2 }> = [
  { key: 'shotSplit', label: '拆分镜', icon: Wand2 },
  { key: 'frameStoryboard', label: '多帧助手', icon: Wand2 },
  { key: 'trinityCards', label: '三合一融合', icon: Sparkles },
  { key: 'modeSwitch', label: '导演模式', icon: ToggleLeft },
];

const TAB_STORAGE_KEY = 'admin:director-ai-config-tab';

function getInitialTab(): SubTab {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    const validKeys = SUB_TABS.map((t) => t.key);
    if (raw && validKeys.includes(raw as SubTab)) return raw as SubTab;
  } catch {
    // ignore localStorage errors
  }
  return 'shotSplit';
}

export default function DirectorAIConfigTab() {
  const [active, setActive] = useState<SubTab>(getInitialTab);

  const handleSetActive = (key: SubTab) => {
    setActive(key);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, key);
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-b border-light-border flex items-center gap-1 overflow-x-auto">
        {SUB_TABS.map((tab) => {
          const Icon = tab.icon;
          const activeFlag = active === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleSetActive(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeFlag
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-light-muted hover:text-light-text'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div>
        {active === 'shotSplit' && <DirectorShotSplitConfigTab />}
        {active === 'frameStoryboard' && <DirectorFrameStoryboardConfigTab />}
        {active === 'trinityCards' && <DirectorTrinityCardsConfigTab />}
        {active === 'modeSwitch' && <DirectorModeSwitchConfigTab />}
      </div>
    </div>
  );
}
